<script lang="ts" setup>
import { onMounted, reactive } from "vue";
import BreadCrumb from "../../components/Common/BreadCrumb.vue";
import Paginador from "@/components/Paginador/Index.vue";
import MsgBox from "@/components/MsgBox/Index.vue";
import DropdownVendedores from "@/components/Dropdowns/DropdownVendedores.vue";
import DropdownClientes from "@/components/Dropdowns/DropdownClientes.vue";

import * as service from "@/services/vouchersService";
import { fdata, f2dec } from "@/utils/funcoes";

const statusOpcoes = [
  { valor: null,                             descricao: "- Todos -" },
  { valor: service.eVoucherStatus.Pendente,  descricao: "Pendente"  },
  { valor: service.eVoucherStatus.Pago,      descricao: "Pago"      },
  { valor: service.eVoucherStatus.Cancelado, descricao: "Cancelado" },
];

function badgeStatus(status: service.eVoucherStatus) {
  if (status === service.eVoucherStatus.Pago)      return "badge-soft-success";
  if (status === service.eVoucherStatus.Cancelado) return "badge-soft-danger";
  return "badge-soft-warning";
}

function labelStatus(status: service.eVoucherStatus) {
  if (status === service.eVoucherStatus.Pago)      return "Pago";
  if (status === service.eVoucherStatus.Cancelado) return "Cancelado";
  return "Pendente";
}

const controle = reactive({
  filtros: {
    voucherId:   null,
    nome:        null,
    vendedorId:  null,
    indicadorId: null,
    indicadoId:  null,
    status:      null,
    paginaAtual: 1,
    tamanhoPagina: 15,
  } as service.Filtros,
  itens: [] as service.Voucher[],
  totalRegistros: 0,
  itemExcluir: null as service.Voucher | null,
  janExcluir: false,
  status: "",
  erro: "",
});

onMounted(() => pesquisar());

function pesquisar() {
  controle.status = "Carregando...";
  controle.erro   = "";
  service.obter(controle.filtros)
    .then(r => {
      controle.itens          = (r as any).dados ?? r;
      controle.totalRegistros = (r as any).totalRegistros ?? controle.itens.length;
      controle.status         = "";
    })
    .catch(e => { controle.status = ""; controle.erro = e.message || String(e); });
}

function pergExcluir(item: service.Voucher) {
  controle.itemExcluir = item;
  controle.janExcluir  = true;
}

function excluir() {
  if (!controle.itemExcluir) return;
  service.excluir(controle.itemExcluir.voucherId)
    .then(() => { controle.janExcluir = false; pesquisar(); })
    .catch(e => { controle.erro = e.message || String(e); controle.janExcluir = false; });
}
</script>

<template>
  <BreadCrumb PageTitle="Vouchers" />

  <div class="card">
    <div class="card-body">
      <div class="row g-2 align-items-end">

        <div class="col-6 col-md-2">
          <label><i class="flaticon-search"></i> Pesquisar</label>
          <input type="text" class="form-control form-control-sm"
            placeholder="Nome do indicado..."
            v-model="controle.filtros.nome"
            @keyup="pesquisar" />
        </div>

        <div class="col-6 col-md-2">
          <label>Vendedor</label>
          <DropdownVendedores v-model="controle.filtros.vendedorId" @change="pesquisar" />
        </div>

        <div class="col-6 col-md-2">
          <label>Status</label>
          <select class="form-select form-select-sm" v-model="controle.filtros.status" @change="pesquisar">
            <option v-for="(op, i) in statusOpcoes" :key="i" :value="op.valor">{{ op.descricao }}</option>
          </select>
        </div>

        <div class="col-6 col-md-2 ms-auto">
          <RouterLink class="btn btn-primary btn-sm float-end" to="/vouchers/cadastro">
            <i class="flaticon-plus"></i> Adicionar
          </RouterLink>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-12 table-responsive">
          <div class="text-danger" v-if="controle.erro">{{ controle.erro }}</div>
          <div class="text-muted"  v-if="controle.status">{{ controle.status }}</div>

          <table class="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Indicador (quem indicou)</th>
                <th>Indicado (novo cliente)</th>
                <th>Vendedor</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Cadastro</th>
                <th>Pagamento</th>
                <th>Opções</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in controle.itens" :key="item.voucherId">
                <td>{{ item.voucherId }}</td>
                <td class="nowrap-celular">{{ item.indicador?.nome ?? '—' }}</td>
                <td class="nowrap-celular">{{ item.indicado?.nome  ?? '—' }}</td>
                <td class="nowrap-celular">{{ item.vendedor?.nomeCompleto ?? '—' }}</td>
                <td>R$ {{ f2dec(item.valor) }}</td>
                <td>
                  <span class="badge rounded-pill" :class="badgeStatus(item.status)">
                    {{ labelStatus(item.status) }}
                  </span>
                </td>
                <td>{{ item.dataCadastro ? fdata(item.dataCadastro) : '—' }}</td>
                <td>{{ item.dataPagamento ? fdata(item.dataPagamento) : '—' }}</td>
                <td class="text-nowrap">
                  <RouterLink :to="`/vouchers/cadastro/${item.voucherId}`" title="Editar" class="me-2">
                    <i class="flaticon-pen fs-5"></i>
                  </RouterLink>
                  <a href="javascript:void(0);" title="Excluir" @click="pergExcluir(item)">
                    <i class="flaticon-delete fs-5 text-danger"></i>
                  </a>
                </td>
              </tr>
              <tr v-if="controle.itens.length === 0 && !controle.status">
                <td colspan="9" class="text-center text-muted py-3">Nenhum registro encontrado.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="card-footer">
      <div class="row mt-1">
        <div class="col-md-4">{{ controle.totalRegistros }} Registros</div>
        <div class="col-md-6">
          <Paginador class="float-end"
            :paginaAtual="controle.filtros.paginaAtual"
            :tamanhoPagina="controle.filtros.tamanhoPagina"
            :totalRegistros="controle.totalRegistros"
            @on-change="(el: any) => {
              controle.filtros.paginaAtual    = el.paginaAtual;
              controle.filtros.tamanhoPagina  = el.tamanhoPagina;
              pesquisar();
            }" />
        </div>
        <div class="col-md-2">
          <RouterLink class="btn btn-secondary btn-sm float-end" to="/">Fechar</RouterLink>
        </div>
      </div>
    </div>
  </div>

  <MsgBox id="janExcluir" titulo="Atenção" :exibir="controle.janExcluir"
    @onFechou="controle.janExcluir = false"
    :botao-sim="true" :botao-nao="true" @onSim="excluir()">
    <div class="fw-bold fs-6">Deseja excluir/cancelar este voucher?</div>
  </MsgBox>
</template>

<style scoped>
a { text-decoration: none; }

/* Evita quebra de linha nas colunas de nome/telefone apenas no celular */
@media (max-width: 767.98px) {
  .nowrap-celular {
    white-space: nowrap;
  }
}
</style>
